import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";

import { trackButtonClick } from "@/utils/analytics";
import { trackFilterInteraction, getSrpLocationLabel } from "@/utils/filterTrackingService";
import { markInternalNavigation } from "@/utils/attributionCapture";
import { getSRPDownload, getSRPPrint } from "@/api/srpFilter";
import { setPrintLoad, setDownloadLoad } from "@/redux/SRP/srpSlice";
import { addIsPartOfCampaign } from "@/redux/SRP/propertiesSlice";
import { toggleGridAndList } from "../../features/filter/filterSlice";
import mapActive from "../../../public/assets/images/srp/mapActive.svg";
import map from "../../../public/assets/images/srp/map.svg";
import listActive from "../../../public/assets/images/srp/listActive.svg";
import list from "../../../public/assets/images/srp/list.svg";
import NearBySvg from "../../../public/assets/svg/srp/NearBySvg";
import ExportSvg from "../../../public/assets/svg/srp/ExportSvg";
import PrintSvg from "../../../public/assets/svg/srp/PrintSvg";
import useWindowDimensions from "@/hooks/useWindowDimensions";

const GridListButton = ({
  isResult = true,
  isMain = true,
  isHideNearMe = false,
  isHidePrint,
  isHideExport,
}) => {
  const { isGridOrList } = useSelector((state) => state.filter);
  const isPrintLoading = useSelector((state) => state.srp.isPrintLoading);
  const isDownloadLoading = useSelector((state) => state.srp.isDownloadLoading);
  const { recordPerPage, enableSignSelrCampaignFlag } = useSelector(
    (state) => state.srp.srpInitData
  );
  const { pathname } = useLocation();
  const srpInitData = useSelector((state) => state.srp.srpInitData);
  const { width } = useWindowDimensions();
  const { state, city, zip, searchBy, polygonId, polygonType } = srpInitData;

  const totalResultCount = srpInitData?.totalResultCount || 0;
  const navigate = useNavigate();
  const {
    listingType,
    propertySubType,
    category,
    propertyStatus,
    occupancyStatus,
    sid,
    propertyShowMoreStatus,
    newToHubzu,
    marketingPhase,
    auctionEndingToday,
    ownItNow,
    nobuyerpremium,
    brokerCoOp,
    cashOnly,
    campaign,
    campaignId,
    campaignType,
    isPartOfCampaign,
    offers,
    bedrooms,
    bathrooms,
    price,
    area,
    builtYear,
    sort,
    states,
    pageNumber,
  } = useSelector((state) => state.properties);
  const dispatch = useDispatch();

  const getParams = useCallback(
    (flag) => {
      const param = {
        searchBy: searchBy || "All States",
        state: state || "",
        zip: zip || "",
        polygonId: polygonId,
        polygonType: polygonType,
        pageSize: flag === "print" ? recordPerPage : totalResultCount,
        page: pageNumber || 1,
        parameterMap: {},
      };
      if (city) {
        param.city = city;
      }
      if ( listingType.length > 0) {
        param.parameterMap.listingType = [listingType];
      }
      if (propertySubType.length > 0) {
        param.parameterMap.propertySubType = propertySubType;
      }
      if (category.length > 0) {
        param.parameterMap.category = category;
      }
      if (propertyStatus.length > 0) {
        param.parameterMap.propertyStatus = propertyStatus;
      }
      if (occupancyStatus.length > 0) {
        param.parameterMap.occupancyStatus = occupancyStatus;
      }
      if (sid.length > 0) {
        param.parameterMap.sid = sid;
      }
      if (propertyShowMoreStatus.length > 0) {
        param.parameterMap.propertyShowMoreStatus = propertyShowMoreStatus;
      }
      if (newToHubzu) {
        param.parameterMap.newToHubzu = [newToHubzu];
      }
      if (marketingPhase) {
        param.parameterMap.marketingPhase = [marketingPhase];
      }
      if (auctionEndingToday) {
        param.parameterMap.auctionEndingToday = [auctionEndingToday];
      }
      if (ownItNow) {
        param.parameterMap.ownItNow = [ownItNow];
      }
      if (nobuyerpremium) {
        param.parameterMap.nobuyerpremium = [nobuyerpremium];
      }
      if (brokerCoOp) {
        param.parameterMap.brokerCoOp = [brokerCoOp];
      }
      if (cashOnly) {
        param.parameterMap.cashOnly = [cashOnly];
      }
      if (campaign) {
        param.parameterMap.campaign = [campaign];
      }
      if (campaignId) {
        param.parameterMap.campaignId = [campaignId];
      }
      if (campaignType) {
        param.parameterMap.campaignType = [campaignType];
      }
      if (isPartOfCampaign) {
        param.parameterMap.isPartOfCampaign = [isPartOfCampaign];
      }
      if (offers) {
        param.parameterMap.offers = [offers];
      }
      if (searchBy) {
        param.parameterMap.searchBy = [searchBy];
      }
      if (bedrooms) {
        param.parameterMap.bed = [bedrooms];
      }
      if (bathrooms) {
        param.parameterMap.bath = [bathrooms];
      }
      // if (states.length > 0) {
      //   param.parameterMap.state = states.map((item) => item.value);
      // }
      if (
        (price.min || price.max) &&
        !(
          (price.min === "0" || price.min === 0) &&
          (price.max === 1000001 || price.max === 0)
        )
      ) {
        param.parameterMap.priceRange = [
          `${price.min || "0"}-${
            !price.max || price.max === 1000001 ? "1000000plus" : price.max
          }`,
        ];
      }
      if (
        (area.min || area.max) &&
        !(
          (area.min === "0" || area.min === 0) &&
          (area.max === 10001 || area.max === 0)
        )
      ) {
        param.parameterMap.squareFeetRange = [
          `${area.min || "0"}-${
            !area.max || area.max === 10001 ? "10000plus" : area.max
          }`,
        ];
      }
      if (
        (builtYear.min || builtYear.max) &&
        !(
          (builtYear.min === 1799 ||
            builtYear.min === 0 ||
            builtYear.min === "0") &&
          (builtYear.max === 2011 ||
            builtYear.max === 0 ||
            builtYear.max === "0")
        )
      ) {
        param.parameterMap.yearBuiltRange = [
          `${
            !builtYear.min || builtYear.min === "0"
              ? "Before 1800"
              : builtYear.min
          }-${
            !builtYear.max || builtYear.max === "0"
              ? "After 2010"
              : builtYear.max
          }`,
        ];
      }
      if (sort?.value) {
        param.parameterMap.sort = [sort?.value.replace("sort=", "")];
      }
      switch (pathname) {
        case "/bank-owned":
          param.parameterMap.category = ["bankowned"];
          break;
        // case '/auctions?sid=signature-seller':
        //   param.parameterMap.sid = ['signature-seller'];
        //   break;
        // case '/offmarket':
        //   param.parameterMap.sid = ['offmarket-properties'];
        //   break;
        case "/foreclosures":
          param.parameterMap.listingType = ["AUCN"];
          param.parameterMap.category = ["foreclosure"];
          break;
        case "/short-sale":
          param.parameterMap.listingType = ["AUCN"];
          param.parameterMap.category = ["SHRT_SALE"];
          break;
        case "/traditional-sale":
          param.parameterMap.listingType = ["TRNL"];
          break;
        default:
          // param.parameterMap.listingType = ['AUCN'];
          break;
      }
      return param;
    },
    [
      pathname,
      listingType,
      propertySubType,
      category,
      propertyStatus,
      occupancyStatus,
      sid,
      propertyShowMoreStatus,
      newToHubzu,
      marketingPhase,
      auctionEndingToday,
      ownItNow,
      nobuyerpremium,
      brokerCoOp,
      cashOnly,
      campaign,
      campaignId,
      campaignType,
      isPartOfCampaign,
      offers,
      searchBy,
      bedrooms,
      bathrooms,
      price,
      area,
      builtYear,
      sort,
      states,
      pageNumber,
    ]
  );

  const onNearByClicked = async () => {
    const param = getParams();

    // Track the nearby properties click event
    trackButtonClick({
      eventName: "get_nearby_properties_click",
      eventSource: "Button Click",
      eventCategory: "NEW-SRP",
      eventLabel: `Location: ${getSrpLocationLabel()}`,
    });

    // Track via filter interaction API
    trackFilterInteraction('FILTER_SELECTED');

    markInternalNavigation();
    navigate(
      `/near-me?${isPartOfCampaign === 'Y' ? "isPartOfCampaign=Y&" : ""}${param.searchBy ? `searchBy=${param.searchBy}` : ""}&blp=Y${
        param?.parameterMap?.category?.length > 0 && param?.parameterMap?.category[0] === "commercial" ? "&listingType=AUCN" : param?.parameterMap?.category?.length > 0 ? `&category=${
          param?.parameterMap?.category[0]}` : ""}${
        listingType ? `&listingType=${listingType}` : ""
      }`
    );
  };

  const onClickDownload = async () => {
    trackButtonClick({
      eventName: "export_button_click",
      eventSource: "Button Click",
      eventCategory: "NEW-SRP",
    });

    dispatch(setDownloadLoad(true));
    const param = getParams();
    await getSRPDownload(param, dispatch);
  };

  const onClickPrint = async () => {
    trackButtonClick({
      eventName: "print_click",
      eventSource: "Print Button Click",
      eventCategory: "NEW-SRP",
    });

    dispatch(setPrintLoad(true));
    const param = getParams("print");
    await getSRPPrint(param, dispatch);
  };
  if (isMain) {
    return (
      <div className="mb-0 mt0 d-flex align-items-right justify-content-end">
        <div className="srpToggleBtnContainer mb-0">
          <span
            className={`srpToggleMap ${isGridOrList ? "active" : ""}`}
            title="map-view"
            onClick={() => {
              dispatch(toggleGridAndList(true));

              trackButtonClick({
                eventName: "open_map_click",
                eventSource: "Toggle map click",
                eventCategory: "NEW-SRP",
                eventLabel: `Location: ${getSrpLocationLabel()}`,
              });
            }}
          >
            <img
              src={isGridOrList ? mapActive : map}
              height={20}
              width={20}
              alt="map"
            />
          </span>
          <span
            className={`srpToggleList ${!isGridOrList ? "active" : ""}`}
            title="list-view"
            onClick={() => {
              dispatch(toggleGridAndList(false));

              trackButtonClick({
                eventName: "close_map_click",
                eventSource: "Toggle map click",
                eventCategory: "NEW-SRP",
                eventLabel: `Location: ${getSrpLocationLabel()}`,
              });
            }}
          >
            <img
              src={!isGridOrList ? listActive : list}
              height={20}
              width={20}
              alt="list-view"
            />
          </span>
        </div>
      </div>
    );
  }
  return (
    <div className={`listing_list_style mb-0 mt0 d-flex flex-wrap align-items-right justify-content-end ${width < 991 ? 'pr0 pl0' : isGridOrList ? '' : 'pr5'}`} role="heading" tabIndex="0" aria-level="2">
      {enableSignSelrCampaignFlag === "Y" && (
        <button
          disabled={isPartOfCampaign === 'Y'}
          className={`list-inline-item promotion-btn ${isPartOfCampaign === 'Y' ? "promotion-btn-dis" : ""}`}
          onClick={() => {
            dispatch(addIsPartOfCampaign({ value: "Y", isUpdate: true }))
            trackButtonClick({
              eventName: "spl_promotions_click",
              eventCategory: "NEW-SRP",
              eventSource: "on special promotion button click"
            })
           }
          }
        >
          <div>Special Promotions</div>
        </button>
      )}
      {!isHideNearMe && (
        <div className="grid-btn-container" onClick={onNearByClicked}>
          <div className="srp-top-icon loc-icon">
            <NearBySvg />
          </div>
          <div className="srp-top-btn pl5">Nearby Properties</div>
        </div>
      )}
      {!isHideExport && isResult && (
        <div className="grid-btn-container" onClick={onClickDownload}>
          {isDownloadLoading ? <div className="pdf-loader" /> : (
            <div className="srp-top-icon">
              <ExportSvg />
            </div>
          )}
          <div className="srp-top-btn pl5">Export</div>
        </div>
      )}
      {!isHidePrint && isResult && (
        <div className="grid-btn-container" onClick={onClickPrint}>
          {isPrintLoading ? <div className="pdf-loader" /> : (
            <div className="srp-top-icon">
              <PrintSvg />
            </div>
          )}
          <div className="srp-top-btn pl5">Print</div>
        </div>
      )}
    </div>
  );
};

export default GridListButton;
